import { Card, ConditionalRenderer, Disclosure, Hr } from "@chef/components";
import { isNotEmptyArray } from "@chef/utils/array";
import { useDiscountData } from "@chef/state-management/hooks";
import { LoyaltyInfoCard } from "@chef/smart-components";

import {
  RedeemDiscount,
  RedeemGiftcard,
  VoucherCard,
} from "../components/DiscountAndGiftcard";
import { MyAccountLayout } from "../components/MyAccountLayout";
import { PointDetails } from "../components/PointDetails";

import { intl } from "./MyAccountDiscountsAndGiftcardsPage.Intl";

export const MyAccountDiscountsAndGiftcardsPage = () => {
  const { discounts, giftCards, credits, shouldRenderExpiredCoupons } =
    useDiscountData();

  const { usedDiscounts, expiredDiscounts, activeDiscounts } = discounts;
  const { expiredCredits, activeCredits } = credits;

  const {
    permanentDiscounts: { nextPermanentDiscount, otherPermanentDiscounts },
    temporaryDiscounts: { nextTemporaryDiscount, otherTemporaryDiscounts },
  } = activeDiscounts;

  const { expiredGiftCards, activeGiftCards } = giftCards;

  const activeDiscountsExsits = !!(
    nextTemporaryDiscount ||
    nextPermanentDiscount ||
    isNotEmptyArray(activeGiftCards ?? []) ||
    isNotEmptyArray(activeCredits ?? [])
  );

  return (
    <MyAccountLayout page="discountsAndGiftcards">
      <section className="col-span-5">
        <Card id="discount-code-card">
          <h2 className="mb-6 md:text-xl">
            <strong>
              {intl.REDEEM} {intl.DISCOUNT_CODE}
            </strong>
          </h2>
          <RedeemDiscount />
        </Card>

        <Card className="mt-4" id="gift-code-card">
          <h2 className="mb-6 md:text-xl">
            <strong>
              {intl.REDEEM} {intl.GIFTCARD}
            </strong>
          </h2>
          <RedeemGiftcard />
        </Card>

        <h2 className="mt-10 mb-6 md:text-xl">
          <strong>{intl.YOUR_LOYALTY_LEVEL}</strong>
        </h2>

        <LoyaltyInfoCard className="bg-grey-3" />

        <Card noPadding className="p-6 mt-4">
          <PointDetails />
        </Card>

        <div className="mt-10">
          <h2 className="text-xl">{intl.YOUR_REBATES}</h2>
          <>
            {activeDiscountsExsits && (
              <h4 className="mt-6">
                <strong>{intl.DEDUCTED_ON_NEXT_DELIVERY}</strong>
              </h4>
            )}

            {!!nextTemporaryDiscount && (
              <VoucherCard
                key={nextTemporaryDiscount.discountId}
                id="active-discount-card"
                discount={nextTemporaryDiscount}
                status="active"
              />
            )}

            {!!nextPermanentDiscount && (
              <VoucherCard
                key={nextPermanentDiscount.discountId}
                id="active-discount-card"
                discount={nextPermanentDiscount}
                status="active"
              />
            )}

            {activeGiftCards?.map((giftcard) => (
              <VoucherCard
                id={giftcard.giftCode}
                giftcard={giftcard}
                status="active"
                cardType="giftcard"
                key={giftcard.giftCode}
              />
            ))}

            {activeCredits?.map((credit) => (
              <VoucherCard
                id="active-credit-card"
                credit={credit}
                status="active"
                cardType="credit"
                key={credit.remainingAmount + credit.creditTypeId}
              />
            ))}
          </>

          <ConditionalRenderer
            shouldRender={isNotEmptyArray([
              ...otherTemporaryDiscounts,
              ...otherPermanentDiscounts,
            ])}
          >
            <Hr className="mt-8 mb-6" />
            <Disclosure
              Title={() => <strong>{intl.UPCOMING_DISCOUNT}</strong>}
              defaultOpen
            >
              {[...otherTemporaryDiscounts, ...otherPermanentDiscounts].map(
                (discount) => (
                  <VoucherCard
                    id={discount.discountId}
                    discount={discount}
                    status="upcoming"
                    key={discount.discountId}
                  />
                ),
              )}
            </Disclosure>
          </ConditionalRenderer>

          <ConditionalRenderer shouldRender={shouldRenderExpiredCoupons}>
            <Hr className="mt-8 mb-6" />
            <Disclosure
              Title={() => (
                <strong>{intl.EXPIRED_DISCOUNT_AND_GIFTCARDS}</strong>
              )}
            >
              {expiredGiftCards?.map((giftcard) => (
                <VoucherCard
                  id={giftcard.giftCode}
                  giftcard={giftcard}
                  status="expired"
                  key={giftcard.giftCode}
                  cardType="giftcard"
                />
              ))}
              {usedDiscounts?.map((discount) => (
                <VoucherCard
                  id={discount.discountId}
                  discount={discount}
                  status="used"
                  key={discount.discountId}
                  cardType="discount"
                />
              ))}
              {expiredDiscounts?.map((discount) => (
                <VoucherCard
                  id={discount.discountId}
                  discount={discount}
                  status="expired"
                  key={discount.discountId}
                  cardType="discount"
                />
              ))}
              {expiredCredits?.map((credit) => (
                <VoucherCard
                  id={credit.creditTypeId}
                  credit={credit}
                  status="expired"
                  key={credit.creditTypeId}
                  cardType="credit"
                />
              ))}
            </Disclosure>
          </ConditionalRenderer>
        </div>
      </section>
    </MyAccountLayout>
  );
};
